import Link from 'next/link';
import styled, { css } from 'styled-components';

/*
 * Base fills use a doubled class (`&&`). /checkout and /dapp load the
 * @bidalihq/ui stylesheet, whose Tailwind preflight contains
 *
 *   [type='button'] { -webkit-appearance: button; background-color: transparent; background-image: none }
 *
 * An attribute selector ties with a single styled-components class and that
 * stylesheet comes later, so a plain `background-color` would lose and a
 * filled button would turn transparent. Preflight's other button resets
 * (border, padding, font, colour, line-height) use element selectors and lose
 * to any class. Hover and dark rules have the same specificity as `&&`, so
 * they must stay below the base fill.
 *
 * To change the fill in `styled(Button)`, use `&&` there as well.
 */
const variants = {
  default: css`
    color: var(--primary-foreground);

    && {
      background-color: var(--primary);
    }

    &:hover {
      background-color: color-mix(in oklab, var(--primary) 80%, transparent);
    }
  `,
  outline: css`
    border-color: var(--border);
    color: var(--foreground);

    && {
      background-color: var(--background);
    }

    &:hover,
    &[aria-expanded='true'] {
      background-color: var(--muted);
    }

    .dark & {
      background-color: color-mix(in oklab, var(--input) 30%, transparent);
      border-color: var(--input);

      &:hover,
      &[aria-expanded='true'] {
        background-color: color-mix(in oklab, var(--input) 50%, transparent);
      }
    }
  `,
  secondary: css`
    color: var(--secondary-foreground);

    && {
      background-color: var(--secondary);
    }

    &:hover {
      background-color: color-mix(in oklch, var(--secondary), var(--foreground) 5%);
    }
  `,
  ghost: css`
    background-color: transparent;
    color: var(--foreground);

    &:hover,
    &[aria-expanded='true'] {
      background-color: var(--muted);
    }

    .dark & {
      &:hover,
      &[aria-expanded='true'] {
        background-color: color-mix(in oklab, var(--muted) 50%, transparent);
      }
    }
  `,
  link: css`
    background-color: transparent;
    color: var(--primary);
    text-underline-offset: 4px;

    &:hover {
      text-decoration: underline;
    }
  `,
  // White on the brand gradient in both themes (CtaPanel primary action).
  // Where supported, the label is painted with the brand gradient: the top
  // background layer is clipped to the text, the white layer to the box.
  inverse: css`
    box-shadow: var(--shadow-md);
    color: var(--brand-indigo);
    font-weight: 600;

    && {
      background-color: #ffffff;
    }

    &:hover {
      background-color: rgb(255 255 255 / 0.9);
      box-shadow: var(--shadow-lg);
    }

    @supports ((background-clip: text, padding-box) or (-webkit-background-clip: text, padding-box)) {
      color: transparent;

      && {
        -webkit-background-clip: text, padding-box;
        background-clip: text, padding-box;
        background-color: transparent;
        background-image: var(--gradient-brand), linear-gradient(#ffffff, #ffffff);
      }

      svg {
        color: var(--brand-indigo);
      }

      &:hover {
        background-color: transparent;
        background-image:
          var(--gradient-brand), linear-gradient(rgb(255 255 255 / 0.9), rgb(255 255 255 / 0.9));
      }
    }
  `,
  // Translucent pill for secondary actions on the brand gradient.
  glass: css`
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    border-color: rgb(255 255 255 / 0.4);
    color: #ffffff;

    && {
      background-color: rgb(255 255 255 / 0.15);
    }

    &:hover {
      background-color: rgb(255 255 255 / 0.25);
      border-color: rgb(255 255 255 / 0.6);
    }
  `,
};

const sizes = {
  sm: css`
    border-radius: var(--radius-md);
    font-size: 13px;
    gap: 4px;
    height: 28px;
    padding: 0 10px;

    svg {
      height: 14px;
      width: 14px;
    }
  `,
  default: css`
    height: 32px;
    padding: 0 10px;
  `,
  lg: css`
    height: 36px;
    padding: 0 12px;
  `,
  // The marketing call to action: 44px tall, fully rounded, 16px text.
  pill: css`
    border-radius: 9999px;
    font-size: 16px;
    gap: 8px;
    height: 44px;
    padding: 0 20px;
  `,
  // Square icon button (navbar controls). Always pass an aria-label.
  icon: css`
    height: 36px;
    padding: 0;
    width: 36px;

    svg {
      height: 20px;
      width: 20px;
    }
  `,
};

const isInternal = (href) => typeof href === 'string' && /^\/(?!\/)/.test(href);

/**
 * Button or link, styled the same.
 *
 *   <Button onClick={...}>Add to cart</Button>
 *   <Button $variant="outline" $size="pill" href="/buy">Browse gift cards</Button>
 *   <Button $variant="ghost" $size="icon" aria-label="Close"><X size={20} /></Button>
 *
 * $variant: default | outline | secondary | ghost | link | inverse | glass
 * $size:    sm | default | lg | pill | icon
 * $fullWidth: stretch to the container width
 *
 * With `href` it renders next/link for internal paths and a plain <a> for
 * everything else; pass `as` to override the element. It is a plain styled
 * component, so `styled(Button)` works and the style props stay transient
 * (nothing leaks to the DOM).
 */
const Button = styled.button.attrs(({ as, disabled, href }) => {
  const isLink = href != null;

  return {
    as: as || (isLink ? (isInternal(href) ? Link : 'a') : 'button'),
    // Links have no native disabled state.
    ...(isLink && disabled ? { 'aria-disabled': 'true', tabIndex: -1 } : null),
  };
})`
  align-items: center;
  background-clip: padding-box;
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  justify-content: center;
  letter-spacing: normal;
  line-height: 1;
  margin: 0;
  outline: none;
  position: relative;
  text-decoration: none;
  text-transform: none;
  transition:
    background-color 150ms,
    border-color 150ms,
    box-shadow 150ms,
    color 150ms,
    transform 150ms;
  user-select: none;
  white-space: nowrap;

  && {
    appearance: none;
  }

  svg {
    flex-shrink: 0;
    height: 16px;
    pointer-events: none;
    width: 16px;
  }

  &:focus-visible {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 50%, transparent);
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
    pointer-events: none;
  }

  ${({ $size }) => sizes[$size] || sizes.default};
  ${({ $variant }) => variants[$variant] || variants.default};

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `};

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

export default Button;
